/*
 * 表格页面的通用方法
 * @Author: anker.wang
 * @Date: 2018-01-08 11:25:59
 * @Last Modified by: anker.wang
 * @Last Modified time: 2018-04-25 11:53:19
 * @Description 路由参数自动同步到搜索参数;在路由更新时获得表格数据;使用分页和搜索事件自动变更路由
 */

import xlsx from './xlsx-v1'
import filter from '@core/filters'
import moment from 'moment'

export default {
  data () {
    return {
      // 搜索表单字段
      search: {},
      // 搜索字段申明
      searchFieldType: {},
      // 表格数据
      table: [],
      // 分页参数
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 1,
      },
    }
  },
  // 路由进入时
  beforeRouteEnter (to, from, next) {
    // 在导航转入新的路由前
    next(vm => {
      // 处理路由参数
      let query = vm.handleSearchFieldType(to.query)
      // 设置搜索参数
      vm.setSearchParams(query)
      // 获取表格数据
      vm.handleTableDataRequest(query)
    })
  },
  // 路由更新时
  beforeRouteUpdate (to, from, next) {
    // 处理路由参数
    let query = this.handleSearchFieldType(to.query)
    // 获取表格数据
    this.handleTableDataRequest(query)
    // 继续加载路由
    next()
  },
  methods: {
    /**
     * 获得表格数据
     * @param {*} params
     * @typedef 必须重写
     */
    getTableData (params) {
      // 需要重写该方法 返回 Promise 对象
    },
    /**
     * 设置表格数据
     * @param {*} params
     * @typedef 可选重写
     */
    setTableData (params) {
      this.table = params
    },
    /**
     * 获得搜索参数的类型声明
     * @description 从路由上同步参数时会造成获得全部都是字符串
     * @typedef 可选重写
     */
    getSearchFieldType () {
      return this.searchFieldType
    },
    /**
     * 获取分页数据
     * @param {*} params
     * @typedef 可选重写
     */
    getPagination () {
      return {
        current_page: this.pagination.current_page,
        per_page: this.pagination.per_page,
      }
    },
    /**
     * 设置分页数据
     * @param {*} params
     * @typedef 可选重写
     */
    setPagination (pagination) {
      this.pagination.current_page = Number(pagination.current_page)
      this.pagination.per_page = Number(pagination.per_page)
      this.pagination.total = Number(pagination.total)
    },
    /**
     * 获得搜索参数
     * @description 会同步到路由的query参数中
     * @typedef 可选重写
     */
    getSearchParams () {
      return this.search
    },
    /**
     * 设置搜索参数
     * @typedef 可选重写
     */
    setSearchParams (params) {
      for (const key in params) {
        if (this.search.hasOwnProperty(key)) {
          this.search[key] = params[key]
        }
      }
    },
    /**
     * 获得获得路由参数
     * @description
     * @typedef 可选重写
     */
    getRouterQueryParams () {
      return this.handleSearchFieldType(this.$route.query)
    },
    /**
     * 设置路由参数
     * @description 设置路由参数
     * @typedef 可选重写
     */
    setRouterQueryParams (query = {}) {
      this.$router.replace({
        query: {
          ...query,
          // 用于相同的搜索参数时也触发路由更新
          '-_-': moment().format('HHmmss'),
        },
      })
    },
    /**
     * 获得搜索表单的ref名称
     * @typedef 可选重写
     */
    getSearchFormRef () {
      return 'searchForm'
    },
    /**
     * 获得请求参数
     * 删除空数组以及空字段
     * @param {*} params
     */
    getRequestParams (params) {
      // 设定的搜索参数
      let searchParams = this.getSearchParams()
      // 请求参数
      let requestParams = {...searchParams, ...params}
      // 删除空的请求参数
      for (const key in requestParams) {
        // 为数组时必须长度大于0
        if (requestParams[key] instanceof Array && requestParams[key].length === 0) {
          delete requestParams[key]
        }
        // 空字符串或者null时删除字段
        if (requestParams[key] === '' || requestParams[key] === null) {
          delete requestParams[key]
        }
      }
      return requestParams
    },
    /**
     * 设置请求参数到路由参数
     * 路由在改变参数以后会自动获得表格数据
     */
    setRequestParamsToRouter (params) {
      // 获得分页参数
      let pagination = this.getPagination()
      // 获得所有请求参数
      let requestParams = this.getRequestParams()
      // 设置路由参数
      this.setRouterQueryParams({
        ...pagination,
        ...requestParams,
        ...params,
      })
    },
    /**
     * 匹配到搜索参数的类型
     * 必须 search 中存在的字段，通过申明的类型转换
     * @param {*} query
     */
    handleSearchFieldType (query) {
      let search = this.getSearchParams()
      let searchType = this.getSearchFieldType()
      let pagination = this.getPagination()
      let params = {}
      // 循环路由参数
      for (const key in query) {
        // 搜索参数
        if (search.hasOwnProperty(key) && query.hasOwnProperty(key)) {
          let value = query[key]
          // 是否存在类型声明
          if (value && searchType.hasOwnProperty(key)) {
            // 根据声明转换数据类型
            value = filter.dataTypeConve(value, searchType[key])
          }
          params[key] = value
        }
        // 分页参数
        if (pagination.hasOwnProperty(key) && query.hasOwnProperty(key)) {
          params[key] = Number(query[key])
        }
      }
      return params
    },
    /**
     * 处理表格数据请求
     * @param {*} params
     */
    handleTableDataRequest (params) {
      params = this.handleRequestParams({
        ...this.getPagination(),
        ...this.getRequestParams(),
        ...params,
      })
      // 获得表格数据
      this.getTableData(params).then((res) => {
        this.setTableData(res.data.data)
        this.setPagination({
          current_page: res.data.current_page,
          per_page: res.data.per_page,
          total: res.data.total,
        })
      })
    },
    /**
     * 对发送请求的参数进行处理
     * @param {*} params
     * @typedef 可选重写
     */
    handleRequestParams (params) {
      return params
    },
    /**
     * 处理分页大小时的变化
     * @param {*} val
     */
    handleSizePageChange (val) {
      let pagination = this.getPagination()
      if (pagination.per_page !== val) {
        pagination.per_page = val
        this.setRouterQueryParams({...this.getRouterQueryParams(), ...pagination})
      }
    },
    /**
     * 处理页码变化
     * @param {*} val
     */
    handleCurrentPageChange (val) {
      let pagination = this.getPagination()
      if (pagination.current_page !== val) {
        pagination.current_page = val
        this.setRouterQueryParams({...this.getRouterQueryParams(), ...pagination})
      }
    },
    /**
     * 搜索列表方法
     * @param {*} params
     */
    onSearch (params) {
      // 验证填写参数
      let searchForm = this.$refs[this.getSearchFormRef()]
      if (searchForm) {
        searchForm.validate((valid) => {
          if (valid) {
            this.setRequestParamsToRouter(params)
          } else {
            this.$message.error('搜索内容填写不正确！')
          }
        })
      } else {
        this.setRequestParamsToRouter(params)
      }
    },
    /**
     * 重置表单方法
     */
    resetForm () {
      this.$refs[this.getSearchFormRef()].resetFields()
    },
    exportExcelFile (name, request) {
      let fileName = xlsx.makeName(name)
      this.$store.commit('VISIBLE_LAYOUT_EXPORT_DIALOG', true)
      this.$store.commit('SET_LAYOUT_EXPORT_NAME', fileName + '.xlsx')
      request.then(res => {
        xlsx.exportFile(res.data.data, {fileName})
        this.$store.commit('SET_LAYOUT_EXPORT_PERCENTAGE', 100)
        this.$store.commit('SET_LAYOUT_EXPORT_STATUS', 'success')
      }).catch(() => {
        this.$store.commit('SET_LAYOUT_EXPORT_STATUS', 'exception')
      })
    },
    /**
     * 导出EXCEL文件
     * @param {*} name
     * @param {*} params
     */
    exportMoreExcelFile (name, params) {
      let self = this
      let fileName = xlsx.makeName(name)
      let _totalRow = 0
      let _currentTotalRow = 0
      let _params = this.handleRequestParams({
        ...this.getPagination(),
        ...this.getRouterQueryParams(),
        ...params,
      })
      xlsx.requestExportFile(this.getTableData, {..._params, ...params}, {
        fileName,
        titles: {title: '标题'},
        ArrayObjects: true,
        onStart (totalPage, totalRow) {
          _totalRow = totalRow
          self.$store.commit('VISIBLE_LAYOUT_EXPORT_DIALOG', true)
          self.$store.commit('SET_LAYOUT_EXPORT_NAME', fileName + '-' + totalRow + '.xlsx')
        },
        onFinish (data) {
          self.$store.commit('SET_LAYOUT_EXPORT_STATUS', 'success')
        },
        onProgress (data, total) {
          _currentTotalRow += data.data.length
          self.$store.commit('SET_LAYOUT_EXPORT_PERCENTAGE', Math.floor(_currentTotalRow / _totalRow * 100))
        },
        onError (error) {
          self.$store.commit('SET_LAYOUT_EXPORT_STATUS', 'exception')
          throw new Error(error)
        },
      })
    },
  },
}



// WEBPACK FOOTER //
// ./src/mixins/table-v1.js